import axios from 'axios'
import type { InsuranceParamsDto, InsuranceParamsPagedGrid, InsurerDiscount, PoleasQuery, SimpleItem } from 'BrokerTsLib/src/dtos'
import type { GenericQueryOptions } from 'BrokerTsLib/src/hooks'
import { appendToFormData, longStaleTime } from 'BrokerTsLib/src/hooks'
import { useCallback, useRef } from 'react'
import { useMutation, useQuery } from 'react-query'

import { useAuth } from '@/context/AuthContext'
import type { ClientDisabledRequest, NewPoleasImport, NewPoleasImportResponse, PoleasImportPreviewRequest, PoleasImportRequest } from '@/dtos/PoleasImport'

import { returnArray } from 'BrokerTsLib/src/Utils/reactQueryUtils'
import { useUrl } from '.'
import { Apis } from '../appSettings/AppSettings'

export const useGetAll = () => {
  const url = useUrl(Apis.poleas)

  return useMutation((search: PoleasQuery) =>
    axios.post<InsuranceParamsPagedGrid>(`${url}/GetImports`, search))
}


export const useDeleteInsuranceParams = () => {
  const url = useUrl(Apis.poleas)
  return useMutation((data: { paramIds?: string[] }) =>
    axios.post<{ success: boolean; errors?: number[] }>(`${url}/DeleteInsuranceParams`, data),
  )
}

export const useMarkForExport = () => {
  const url = useUrl(Apis.poleas)
  return useMutation((data: { paramIds?: string[] }) =>
    axios.post<{ success: boolean; errors?: number[], importId?: number }>(`${url}/MarkExported`, data),
  )
}

export const useGetPoleasExportUrl = () => {
  const url = useUrl(Apis.poleas)

  return (importId?: number) => `${url}/Export?id=${importId}`
}

export const useGetAllInsurersAndDiscounts = (options?: GenericQueryOptions<InsurerDiscount[]>) => {
  const url = useUrl(Apis.poleas)
  const { user } = useAuth()

  return useQuery(['useGetAllInsurersAndDiscounts', user?.ouId],
    () => { return axios.get<InsurerDiscount[]>(`${url}/GetAvailableInsurersAndDiscounts`) },
    { staleTime: longStaleTime, ...options }
  )
}

export const useRemoveClientDisabledFlag = () => {
  const url = useUrl(Apis.poleas)
  return useMutation((paramsId: string) =>
    axios.post<{ success: boolean }>(`${url}/RemoveClientDisabledFlag?paramsId=${paramsId}`, ''),
  )
}

export const useAddClientDisabledFlag = () => {
  const url = useUrl(Apis.poleas)
  return useMutation((data: ClientDisabledRequest) =>
    axios.post<{ success: boolean }>(`${url}/AddClientDisabledFlag`, data),
  )
}

export const useNewPoleasImport = () => {
  const url = useUrl(Apis.poleas)
  return useMutation({
    mutationFn: (data: NewPoleasImport) => {
      const formData = new FormData()

      appendToFormData(formData, data)

      return axios.post<NewPoleasImportResponse>(`${url}/NewImport`, formData)
    }

  })
}

export const usePoleasImportPreview = () => {
  const url = useUrl(Apis.poleas)
  // zavoláním reset dojde ke zrušení requestu
  const abortControllerRef = useRef<AbortController | null>(null)

  const mutation = useMutation({
    mutationFn: (data: PoleasImportPreviewRequest) => {
      abortControllerRef.current = new AbortController()
      return axios.post(`${url}/PreviewImport`, data, { signal: abortControllerRef.current?.signal })
    }
  })

  const reset = useCallback(() => {
    abortControllerRef.current?.abort()
  }, [abortControllerRef.current])

  // const reset = useCallback(() => {
  //     abortControllerRef.current?.abort()
  //     mutation.reset()
  // }, [mutation.reset])    

  return { ...mutation, reset }
}

export const usePoleasImport = () => {
  const url = useUrl(Apis.poleas)
  return useMutation({
    mutationFn: (data: PoleasImportRequest) => {
      return axios.post(`${url}/Import`, data)
    }
  })
}

export const useGetSearchNames = () => {
  const url = useUrl(Apis.poleas)

  return useQuery({
    queryKey: ['useGetSearchNames'],
    queryFn: () => axios.get<SimpleItem[]>(`${url}/GetSearchNames`).then(returnArray),
    staleTime: longStaleTime,
  })
}

export const useGetSearchStates = () => {
  const url = useUrl(Apis.poleas)

  return useQuery({
    queryKey: ['useGetSearchStates'],
    queryFn: () => axios.get<SimpleItem[]>(`${url}/GetSearchStates`).then(returnArray),
    staleTime: longStaleTime,
  })
}

export const useAddInsuranceParamsInfo = () => {
  const url = useUrl(Apis.poleas)
  return useMutation(({
    paramsId,
    data
  }: {
    paramsId: string,
    data: InsuranceParamsDto
  }) =>
    axios.post<{ success: boolean }>(`${url}/AddInsuranceParamsInfo?paramsId=${paramsId}`, data),
  )
}


export const useSendPoleasPublicWizardAccess = () => {
  const url = useUrl(Apis.poleas)

  return useMutation({
    mutationFn: (paramsIds: string[]) => axios.post<{ success: boolean; errors?: number[] }>(
      `${url}/SendAccessEmail`, paramsIds
    )
  })
}


export const useGeneratePublicWizardUrl = () => {
  const url = useUrl(Apis.poleas)

  return useMutation({
    mutationFn: (paramsId: string) => {
      return axios.post<{ url?: string }>(
        `${url}/GeneratePublicUrl`, { paramsId }
      ).then(r => r.data)
    },
  })
}